/**
 * StatCard Component
 *
 * Displays a single metric with optional trend indicator.
 * Used across analytics dashboards for KPI display.
 */

import { Paper, Typography, Box, Skeleton } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';

export interface StatCardProps {
  /** Label for the metric */
  title: string;
  /** The metric value (raw number, will be formatted based on format prop) */
  value: number | string;
  /** Optional subtitle text */
  subtitle?: string;
  /** Percent change from previous period (positive = up, negative = down) */
  trend?: number;
  /** How to format the value */
  format?: 'number' | 'currency' | 'percentage';
  /** Loading state */
  loading?: boolean;
}

/**
 * Format a value based on the specified format type
 */
function formatValue(value: number | string, format: 'number' | 'currency' | 'percentage'): string {
  if (typeof value === 'string') {
    return value;
  }

  switch (format) {
    case 'currency':
      // Value is in cents, convert to dollars
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value / 100);

    case 'percentage':
      return `${value.toFixed(1)}%`;

    case 'number':
    default:
      // Format with thousand separators
      return new Intl.NumberFormat('en-US').format(value);
  }
}

/**
 * Get trend icon and color based on trend value
 */
function getTrendInfo(trend: number): {
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  label: string;
} {
  if (trend > 0) {
    return {
      icon: <TrendingUp fontSize="small" />,
      color: '#10B981', // Green
      bgColor: 'rgba(16, 185, 129, 0.1)',
      label: `+${trend.toFixed(1)}%`,
    };
  } else if (trend < 0) {
    return {
      icon: <TrendingDown fontSize="small" />,
      color: '#EF4444', // Red
      bgColor: 'rgba(239, 68, 68, 0.1)',
      label: `${trend.toFixed(1)}%`,
    };
  } else {
    return {
      icon: <TrendingFlat fontSize="small" />,
      color: '#6B7280', // Gray
      bgColor: 'rgba(107, 114, 128, 0.1)',
      label: '0%',
    };
  }
}

export default function StatCard({
  title,
  value,
  subtitle,
  trend,
  format = 'number',
  loading = false,
}: StatCardProps) {
  const trendInfo = trend !== undefined ? getTrendInfo(trend) : null;
  const formattedValue = formatValue(value, format);

  // Build accessible label for screen readers
  const trendDescription =
    trend !== undefined
      ? `, ${trend > 0 ? 'up' : trend < 0 ? 'down' : 'unchanged'} ${Math.abs(trend).toFixed(1)} percent`
      : '';
  const subtitleDescription = subtitle ? `, ${subtitle}` : '';
  const ariaLabel = `${title}: ${formattedValue}${trendDescription}${subtitleDescription}`;

  // Generate unique ID for label association
  const labelId = `stat-${title.toLowerCase().replace(/\s+/g, '-')}-label`;

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          height: '100%',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
        aria-busy="true"
        aria-label={`Loading ${title}`}
      >
        <Skeleton variant="text" width="60%" height={24} />
        <Skeleton variant="text" width="80%" height={40} sx={{ my: 1 }} />
        <Skeleton variant="text" width="40%" height={20} />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'box-shadow 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        },
      }}
      role="figure"
      aria-label={ariaLabel}
    >
      {/* Title */}
      <Typography
        variant="body2"
        color="text.secondary"
        fontWeight={500}
        sx={{ mb: 1 }}
        id={labelId}
      >
        {title}
      </Typography>

      {/* Value */}
      <Typography
        variant="h4"
        fontWeight={600}
        sx={{ mb: 1, lineHeight: 1.2 }}
        aria-describedby={labelId}
      >
        {formattedValue}
      </Typography>

      {/* Trend and Subtitle Row */}
      <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
        {trendInfo && (
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{
              color: trendInfo.color,
              bgcolor: trendInfo.bgColor,
              px: 1,
              py: 0.25,
              borderRadius: 1,
            }}
            aria-hidden="true" // Already included in ariaLabel
          >
            {trendInfo.icon}
            <Typography variant="body2" fontWeight={500}>
              {trendInfo.label}
            </Typography>
          </Box>
        )}

        {subtitle && (
          <Typography variant="body2" color="text.secondary" aria-hidden="true">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
